import React from 'react';
import { api, mutate } from '../../shared/api';
import { Btn, ui } from '../../shared/ui';
import {
    BUY, SELL, GOLD, OZ, Chip, Field, Modal, eur, grams, pct, dt, inputStyle, isHit, distancePct, parseNum,
    type Meta, type Side, type Trigger,
} from './ptShared';

const QTY_CHIPS: Array<[string, number]> = [['1 oz', OZ], ['50 g', 50], ['100 g', 100], ['250 g', 250], ['500 g', 500], ['1 kg', 1000], ['12,5 kg (LBMA)', 12500]];

/** Novi okidac ili izmjena aktivnog. */
export function TriggerFormModal({ token, meta, edit, onClose, onSaved }: { token: string; meta: Meta; edit: Trigger | null; onClose: () => void; onSaved: (msg: string) => void }) {
    const spot = meta.spot.eur_per_g;
    const [client, setClient] = React.useState(edit?.client_name ?? '');
    const [side, setSide] = React.useState<Side>(edit?.side ?? 'buy');
    const [qty, setQty] = React.useState(edit ? String(edit.quantity_g).replace('.', ',') : '');
    const [unit, setUnit] = React.useState<'g' | 'oz'>('g');
    const [target, setTarget] = React.useState(edit ? String(edit.target_eur_per_g).replace('.', ',') : '');
    const [near, setNear] = React.useState(edit?.near_pct != null ? String(edit.near_pct) : '1');
    const [until, setUntil] = React.useState(edit?.valid_until?.slice(0, 10) ?? '');
    const [emails, setEmails] = React.useState(edit?.notify_emails ?? '');
    const [note, setNote] = React.useState(edit?.note ?? '');
    const [busy, setBusy] = React.useState(false);
    const [err, setErr] = React.useState<string | null>(null);

    const qtyNum = parseNum(qty);
    const qtyG = qtyNum == null ? null : unit === 'oz' ? qtyNum * OZ : qtyNum;
    const tgt = parseNum(target);
    const hit = tgt != null && spot > 0 && isHit(side, tgt, spot);
    const dist = tgt != null && spot > 0 ? distancePct(side, tgt, spot) : null;
    const far = tgt != null && spot > 0 && (tgt > spot * 2 || tgt < spot * 0.5);
    const c = side === 'buy' ? BUY : SELL;

    const setRel = (p: number) => {
        if (spot <= 0) return;
        const v = side === 'buy' ? spot * (1 - p / 100) : spot * (1 + p / 100);
        setTarget(v.toFixed(2).replace('.', ','));
    };

    const save = async () => {
        setErr(null);
        if (!client.trim()) return setErr('Upišite klijenta.');
        if (qtyG == null || qtyG <= 0) return setErr('Količina mora biti veća od nule.');
        if (tgt == null || tgt <= 0) return setErr('Upišite ciljnu cijenu po gramu.');
        const body: Record<string, unknown> = {
            client_name: client.trim(), side, quantity_g: Math.round(qtyG * 1000) / 1000, target_eur_per_g: tgt,
            near_pct: near === '' ? null : Number(near), valid_until: until || null, notify_emails: emails.trim() || null, note: note.trim() || null,
        };
        setBusy(true);
        try {
            if (edit) {
                await mutate(`/price-triggers/${edit.id}`, token, 'PATCH', body);
                onSaved('Okidač je spremljen.');
            } else {
                const r = await mutate<{ already_hit: boolean }>('/price-triggers', token, 'POST', body);
                onSaved(r.already_hit ? 'Okidač je spremljen. Cilj je već dosegnut: mail stiže pri sljedećoj provjeri svježeg spota.' : 'Okidač je spremljen i prati se.');
            }
        } catch (e) {
            setErr(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal title={edit ? `Izmjena okidača #${edit.id}` : 'Novi okidač'} onClose={onClose} width={640}
            footer={<><Btn onClick={onClose}>Odustani</Btn><Btn variant="primary" onClick={save} disabled={busy}>{busy ? 'Spremam…' : 'Spremi okidač'}</Btn></>}>
            <div style={{ display: 'grid', gap: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {(['buy', 'sell'] as Side[]).map((s) => {
                        const on = side === s; const col = s === 'buy' ? BUY : SELL;
                        return (
                            <button key={s} type="button" onClick={() => setSide(s)} style={{ padding: '14px 12px', borderRadius: 12, cursor: 'pointer', textAlign: 'left', border: `2px solid ${on ? col : ui.line}`, background: on ? col + '0f' : '#fff' }}>
                                <div style={{ fontWeight: 800, color: on ? col : ui.ink2, fontSize: '1rem' }}>{s === 'buy' ? '▼ Kupiti' : '▲ Prodati'}</div>
                                <div style={{ fontSize: '0.76rem', color: ui.muted, marginTop: 3 }}>{s === 'buy' ? 'okida kad spot padne na cilj ili ispod' : 'okida kad spot naraste na cilj ili iznad'}</div>
                            </button>
                        );
                    })}
                </div>

                <Field label="Klijent">
                    <input list="pt-clients" value={client} onChange={(e) => setClient(e.target.value)} placeholder="Ime i prezime ili tvrtka" style={inputStyle} autoFocus />
                    <datalist id="pt-clients">{meta.clients.map((c0) => <option key={c0} value={c0} />)}</datalist>
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <Field label="Količina" hint={qtyG != null && unit === 'oz' ? `= ${grams(qtyG)} g` : undefined}>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="decimal" placeholder="npr. 250" style={inputStyle} />
                            <select value={unit} onChange={(e) => setUnit(e.target.value as 'g' | 'oz')} style={{ ...inputStyle, width: 70 }}>
                                <option value="g">g</option><option value="oz">oz</option>
                            </select>
                        </div>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                            {QTY_CHIPS.map(([l, g]) => <Chip key={l} active={unit === 'g' && qtyG != null && Math.abs(qtyG - g) < 0.001} onClick={() => { setUnit('g'); setQty(String(Math.round(g * 1000) / 1000).replace('.', ',')); }}>{l}</Chip>)}
                        </div>
                    </Field>
                    <Field label="Ciljna cijena EUR/g" hint={tgt != null ? `= ${eur(tgt * OZ)} EUR/oz` : 'Cijena po gramu, ne po unci.'}>
                        <input value={target} onChange={(e) => setTarget(e.target.value)} inputMode="decimal" placeholder={spot > 0 ? eur(spot) : ''} style={{ ...inputStyle, borderColor: far ? SELL : ui.line }} />
                        {spot > 0 ? (
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                <Chip onClick={() => setTarget(spot.toFixed(2).replace('.', ','))}>spot</Chip>
                                {[0.5, 1, 2, 3, 5].map((p) => <Chip key={p} onClick={() => setRel(p)}>{side === 'buy' ? '−' : '+'}{String(p).replace('.', ',')} %</Chip>)}
                            </div>
                        ) : null}
                    </Field>
                </div>

                <div style={{ borderRadius: 12, padding: '12px 14px', background: hit ? GOLD + '14' : c + '0b', border: `1px solid ${hit ? GOLD + '55' : c + '2a'}`, fontSize: '0.86rem', color: ui.ink }}>
                    {tgt == null || spot <= 0 ? (
                        <span style={{ color: ui.muted }}>Spot sada {spot > 0 ? `${eur(spot)} EUR/g` : 'nije dostupan'}. Upišite cilj za pregled.</span>
                    ) : far ? (
                        <span style={{ color: SELL, fontWeight: 650 }}>Cilj je više od 50 % daleko od spota ({eur(spot)} EUR/g). Provjerite je li upisana cijena po unci umjesto po gramu.</span>
                    ) : hit ? (
                        <span><b>Cilj je već dosegnut</b> (spot {eur(spot)} EUR/g). Okidač će javiti pri prvoj provjeri svježeg spota.</span>
                    ) : (
                        <span>
                            {side === 'buy' ? 'Kupiti' : 'Prodati'} <b>{qtyG != null ? grams(qtyG) : '…'} g</b> kad spot {side === 'buy' ? 'padne na' : 'naraste na'} <b>{eur(tgt)} EUR/g</b>.
                            Spot sada {eur(spot)}, do cilja još <b>{pct(dist, 2)}</b>.
                            {qtyG != null ? <> Vrijednost po cilju {eur(qtyG * tgt)} EUR.</> : null}
                        </span>
                    )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <Field label="Rani signal" hint="Jedan mail kad je spot toliko blizu cilja.">
                        <select value={near} onChange={(e) => setNear(e.target.value)} style={inputStyle}>
                            <option value="">bez ranog signala</option>
                            {['0.5', '1', '2', '3', '5'].map((v) => <option key={v} value={v}>na {v.replace('.', ',')} % od cilja</option>)}
                        </select>
                    </Field>
                    <Field label="Vrijedi do" hint="Prazno = dok se ne okine ili odbaci.">
                        <input type="date" value={until} onChange={(e) => setUntil(e.target.value)} style={inputStyle} />
                    </Field>
                </div>

                <Field label="Dodatni primatelji maila" hint={`Mail uvijek ide vama (${meta.me.email}). Više adresa odvojite zarezom.`}>
                    <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="trgovac@aurodomus.hr" style={inputStyle} />
                </Field>
                <Field label="Napomena">
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} style={{ ...inputStyle, height: 'auto', padding: 10, resize: 'vertical', fontFamily: 'inherit' }} />
                </Field>
                {err ? <div style={{ color: SELL, fontSize: '0.85rem', fontWeight: 650 }}>{err}</div> : null}
            </div>
        </Modal>
    );
}

/** Izvrseno: ostvarena cijena i kolicina. */
export function ExecuteModal({ token, t, onClose, onSaved }: { token: string; t: Trigger; onClose: () => void; onSaved: (m: string) => void }) {
    const [price, setPrice] = React.useState(String(t.triggered_spot_eur_per_g ?? t.target_eur_per_g).replace('.', ','));
    const [qty, setQty] = React.useState(String(t.quantity_g).replace('.', ','));
    const [note, setNote] = React.useState('');
    const [err, setErr] = React.useState<string | null>(null);
    const [busy, setBusy] = React.useState(false);
    const p = parseNum(price); const q = parseNum(qty);
    const slip = p != null ? (t.side === 'buy' ? (t.target_eur_per_g - p) : (p - t.target_eur_per_g)) / t.target_eur_per_g * 100 : null;
    const save = async () => {
        if (p == null || p <= 0 || q == null || q <= 0) return setErr('Upišite ostvarenu cijenu i količinu.');
        setBusy(true);
        try {
            await mutate(`/price-triggers/${t.id}/execute`, token, 'POST', { executed_price_eur_per_g: p, executed_qty_g: q, close_note: note || null });
            onSaved('Nalog je označen kao izvršen.');
        } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
    };
    return (
        <Modal title={`Izvršeno: ${t.client_name}`} onClose={onClose} width={480}
            footer={<><Btn onClick={onClose}>Odustani</Btn><Btn variant="primary" onClick={save} disabled={busy}>Potvrdi izvršenje</Btn></>}>
            <div style={{ display: 'grid', gap: 14 }}>
                <div style={{ fontSize: '0.86rem', color: ui.ink2 }}>{t.side === 'buy' ? 'Kupnja' : 'Prodaja'} {grams(t.quantity_g)} g, cilj {eur(t.target_eur_per_g)} EUR/g{t.triggered_spot_eur_per_g ? `, spot kod okidanja ${eur(t.triggered_spot_eur_per_g)}` : ''}.</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Field label="Ostvarena cijena EUR/g" hint={slip != null ? `${slip >= 0 ? 'bolje' : 'lošije'} od cilja ${pct(Math.abs(slip), 2)}` : undefined}>
                        <input value={price} onChange={(e) => setPrice(e.target.value)} style={inputStyle} inputMode="decimal" />
                    </Field>
                    <Field label="Izvršena količina g"><input value={qty} onChange={(e) => setQty(e.target.value)} style={inputStyle} inputMode="decimal" /></Field>
                </div>
                <Field label="Napomena"><input value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} placeholder="npr. broj ponude" /></Field>
                {err ? <div style={{ color: SELL, fontSize: '0.85rem' }}>{err}</div> : null}
            </div>
        </Modal>
    );
}

export function CancelModal({ token, t, onClose, onSaved }: { token: string; t: Trigger; onClose: () => void; onSaved: (m: string) => void }) {
    const [note, setNote] = React.useState('');
    const [err, setErr] = React.useState<string | null>(null);
    const save = async () => {
        try { await mutate(`/price-triggers/${t.id}/cancel`, token, 'POST', { close_note: note || null }); onSaved('Okidač je odbačen.'); }
        catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    };
    return (
        <Modal title={`Odbaci: ${t.client_name}`} onClose={onClose} width={440}
            footer={<><Btn onClick={onClose}>Natrag</Btn><Btn variant="danger" onClick={save}>Odbaci okidač</Btn></>}>
            <Field label="Razlog (neobavezno)"><input value={note} onChange={(e) => setNote(e.target.value)} style={inputStyle} placeholder="npr. klijent odustao" autoFocus /></Field>
            {err ? <div style={{ color: SELL, fontSize: '0.85rem', marginTop: 10 }}>{err}</div> : null}
        </Modal>
    );
}

type Ev = { id: number; type: string; spot_eur_per_g: number | null; detail: string | null; created_at: string; user_name: string | null };
const EV_LABEL: Record<string, string> = {
    created: 'Unesen', edited: 'Izmijenjen', near: 'Rani signal', triggered: 'Okinut', mail_sent: 'Mail poslan', mail_failed: 'Mail nije prošao',
    executed: 'Izvršen', cancelled: 'Odbačen', expired: 'Istekao', reopened: 'Ponovno aktivan', deleted: 'Obrisan',
};

export function EventsModal({ token, t, onClose }: { token: string; t: Trigger; onClose: () => void }) {
    const [rows, setRows] = React.useState<Ev[] | null>(null);
    React.useEffect(() => { api<{ rows: Ev[] }>(`/price-triggers/${t.id}/events`, token).then((r) => setRows(r.rows)).catch(() => setRows([])); }, [t.id, token]);
    return (
        <Modal title={`Povijest #${t.id}: ${t.client_name}`} onClose={onClose} width={560}>
            {rows == null ? <div style={{ color: ui.muted }}>Učitavam…</div> : (
                <div style={{ position: 'relative', paddingLeft: 22 }}>
                    <div style={{ position: 'absolute', left: 6, top: 4, bottom: 4, width: 2, background: ui.line }} />
                    {rows.map((e) => (
                        <div key={e.id} style={{ position: 'relative', marginBottom: 14 }}>
                            <span style={{ position: 'absolute', left: -21, top: 4, width: 12, height: 12, borderRadius: 99, background: e.type === 'mail_failed' ? SELL : e.type === 'triggered' ? GOLD : '#fff', border: `2px solid ${e.type === 'mail_failed' ? SELL : GOLD}` }} />
                            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                <b style={{ color: ui.ink, fontSize: '0.88rem' }}>{EV_LABEL[e.type] ?? e.type}</b>
                                <span style={{ color: ui.muted, fontSize: '0.78rem' }}>{dt(e.created_at)}{e.user_name ? ` · ${e.user_name}` : ''}</span>
                            </div>
                            {e.spot_eur_per_g != null || e.detail ? (
                                <div style={{ fontSize: '0.8rem', color: ui.ink2, marginTop: 2 }}>
                                    {e.spot_eur_per_g != null ? `spot ${eur(e.spot_eur_per_g)} EUR/g` : ''}{e.spot_eur_per_g != null && e.detail ? ' · ' : ''}{e.detail ?? ''}
                                </div>
                            ) : null}
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
}
